import React from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation, Trans } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useThemeChanger } from 'cityads-v3/hooks/theme'
import classes from './Api.scss'

import Balaclava from 'cityads-v3/components/Balaclava'
import Card from 'cityads-v3/components/Card/Card'
import DocumentationForm from 'cityads-v3/components/DocumentationForm'
import PageWithMenu from 'cityads-v3/components/PageWithMenu'
import Typography from '@mui/material/Typography'

import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined'

import { Light as SyntaxHighlighter } from 'react-syntax-highlighter'
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json'
import sunburst from 'react-syntax-highlighter/dist/esm/styles/hljs/sunburst'
import vs from 'react-syntax-highlighter/dist/esm/styles/hljs/vs'

SyntaxHighlighter.registerLanguage('json', json)

export default function Statistics() {
  const { t } = useTranslation(['Publisher'])
  const { theme } = useThemeChanger()
  const isDark = theme.palette.mode === 'dark';

  return (
    <>
      <Helmet>
        <title>{t('api.statistics.title')}</title>
      </Helmet>

      <Balaclava
        title={t('api.statistics.title')}
      />

      <PageWithMenu>
        <Card
          title={t('api.statistics.title')}
          classes={{
            root: classes.apiCard
          }}
          data-page-menu-key={'api.statistics'}
          data-page-menu-icon={<ConfirmationNumberOutlinedIcon />}
          data-page-menu-title={t('api.statistics.title')}
        >
          <DocumentationForm
            documentationContent={(
              <>
                <Typography variant="body2" paragraph>
                  {t('api.statistics.p1')}
                </Typography>

                <Typography variant="body2" paragraph>
                  <Trans t={t} i18nKey={'api.statistics.p2'}>
                    A full description of the request is available in the
                    <Link to="/publisher/api/description">
                      API Description
                    </Link>
                    section; the following will provide information and examples of use in order to get started.
                  </Trans>
                </Typography>
                <Typography variant="body2" paragraph>
                  {t('api.statistics.p3')}
                </Typography>
                <Typography variant="body2" paragraph>
                  {t('api.statistics.p4')}
                </Typography>
                <Typography variant="body2" paragraph>
                  {t('api.statistics.p5')}
                </Typography>
                <Typography variant="body2" paragraph>
                  {t('api.statistics.p6')}
                </Typography>

                <ul>
                  <li>filters: {t('api.statistics.params.filters')}</li>
                  <li>having: {t('api.statistics.params.having')}</li>
                  <li>groupBy: {t('api.statistics.params.groupBy')}</li>
                  <li>metrics: {t('api.statistics.params.metrics')}</li>
                  <li>ordering: {t('api.statistics.params.ordering')}</li>
                  <li>page: {t('api.statistics.params.page')}</li>
                  <li>per_page: {t('api.statistics.params.per_page')}</li>
                </ul>

                <Typography variant="body2" paragraph>
                  {t('api.statistics.p7')}
                </Typography>

                <SyntaxHighlighter
                  language="json"
                  style={isDark ? sunburst : vs}
                >
                  {`{
  "filters": [
    {
      "name": "lead_time",
      "operation": "between",
      "arguments": [
        "2021-04-01T00:00:00+01:00",
        "2021-10-01T00:00:00+01:00"
      ]
    }
  ],
  "having": [
    {
      "name": "click_count",
      "operation": ">",
      "arguments": 0
    }
  ],
  "groupBy": [
    "month",
    "action_id",
    "action_name"
  ],
  "metrics": [
    "click_count",
    "commission_approved_wm",
    "commission_open_wm",
    "approve_rate"
  ],
  "ordering": {
    "click_count": "asc"
  },
  "currency_code": "USD",
  "page": 1,
  "per_page": 25
}`}

                </SyntaxHighlighter>

                <Typography variant="body2" paragraph>
                  <Trans t={t} i18nKey={'api.statistics.p8'}>
                    You can experiment online with the request structure in the
                    <Link to="/publisher/api/description">
                      API Description
                    </Link>
                    section.
                  </Trans>
                </Typography>
                <Typography variant="body2" paragraph>
                  <Trans t={t} i18nKey={'api.statistics.p9'}>
                    For dates, not only constant literals are supported (like 2021-04-01T00: 00: 00 + 01: 00), but also a
                    <a
                      href="https://www.php.net/manual/en/datetime.formats.php#datetime.formats.relative"
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      relative format
                    </a>
                    , E.g. yesterday, first day of week, - 5 day.
                  </Trans>
                </Typography>
                <Typography variant="body2" paragraph>
                  {t('api.statistics.p10')}
                </Typography>
                <Typography variant="body2" paragraph>
                  {t('api.statistics.p11')}
                </Typography>
              </>
            )}
          >

          </DocumentationForm>
        </Card>
      </PageWithMenu>
    </>
  )
}
